/** Max length for persisted share-api refs / HTTP URLs (matches pdf-templates FluentValidation). */
export const MAX_IMAGE_ASSET_REF_LENGTH = 1024;

/** Max upload size for PNG/JPG data-URI embeds (settings dropzone). */
export const MAX_IMAGE_UPLOAD_BYTES = 10 * 1024 * 1024;

const SAFE_DATA_IMAGE_PREFIX = /^data:image\/(png|jpe?g)(;charset=[^;]+)?;base64,/i;

/**
 * Allowlists image URLs safe for HTML `<img>`, CSS `url()`, and react-pdf `<Image>`.
 * Rejects javascript:, SVG data-URIs, and empty/garbage values.
 * Root-relative paths (`/quote-pdf-logo.png`) are allowed for same-origin preview assets.
 */
export function assertSafeImageUrl(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (SAFE_DATA_IMAGE_PREFIX.test(trimmed)) return trimmed;

  // Same-origin static assets (Vite `public/` or CRM host public logos).
  if (trimmed.startsWith('/') && !trimmed.startsWith('//') && !trimmed.includes('\\')) {
    return trimmed;
  }

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
      return trimmed;
    }
  } catch {
    return null;
  }

  return null;
}

/** True when the value is an inline raster data-URI (client-only until share-api upload). */
export function isInlineImageDataUri(value: string | null | undefined): boolean {
  if (typeof value !== 'string') return false;
  return SAFE_DATA_IMAGE_PREFIX.test(value.trim());
}

/**
 * Wire-safe background/logo asset ref for pdf-templates PUT.
 * Data-URIs and overlong strings are omitted (BE max 1024; contract forbids image bytes).
 */
export function toPersistedImageAssetRef(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || isInlineImageDataUri(trimmed)) return null;
  if (trimmed.length > MAX_IMAGE_ASSET_REF_LENGTH) return null;

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return assertSafeImageUrl(trimmed);
  }

  // Share-api blob key (opaque id) — no scheme.
  if (/^[\w./:@-]+$/.test(trimmed)) return trimmed;
  return null;
}
